import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  Link,
  Grid
} from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import TimezoneSelector from './TimezoneSelector';
import LandingLayout from './LandingLayout';
import ProgressBar from './ProgressBar';

const Signup: React.FC = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [timezone, setTimezone] = useState('America/Los_Angeles');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState<{
    valid: boolean;
    issues: string[];
    score: number;
  }>({ valid: false, issues: [], score: 0 });
  const navigate = useNavigate();
  const { login } = useAuth();

  // Real-time password validation
  const validatePassword = (pwd: string) => {
    const issues: string[] = [];
    let score = 0;

    if (pwd.length < 6) {
      issues.push('Must be at least 6 characters long');
    } else {
      score += 1;
    }

    const hasLowercase = /[a-z]/.test(pwd);
    const hasUppercase = /[A-Z]/.test(pwd);
    const hasNumbers = /\d/.test(pwd);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(pwd);

    if (hasLowercase) {
      score += 1;
    } else {
      issues.push('Include lowercase letters');
    }

    if (hasUppercase) {
      score += 1;
    } else {
      issues.push('Include uppercase letters');
    }

    if (hasNumbers) {
      score += 1;
    } else {
      issues.push('Include numbers');
    }

    if (hasSpecial) {
      score += 1;
    } else {
      issues.push('Include special characters');
    }

    // Check for common patterns
    const commonPatterns = ['123', 'abc', 'qwe', 'password', 'admin', 'user'];
    const hasCommonPattern = commonPatterns.some(pattern => 
      pwd.toLowerCase().includes(pattern)
    );

    if (hasCommonPattern) {
      issues.push('Avoid common patterns');
      score -= 1;
    }

    const valid = pwd.length >= 6 && score >= 2 && !hasCommonPattern;

    return { valid, issues, score };
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    if (newPassword.length > 0) {
      const validation = validatePassword(newPassword);
      setPasswordValidation(validation);
    } else {
      setPasswordValidation({ valid: false, issues: [], score: 0 });
    }
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous messages
    setError('');
    setSuccess('');
    setLoading(true);
    
    // Validation
    if (!firstName.trim()) {
      setError('First name is required');
      setLoading(false);
      return;
    }
    
    if (!lastName.trim()) {
      setError('Last name is required');
      setLoading(false);
      return;
    }
    
    if (!email.trim()) {
      setError('Email address is required');
      setLoading(false);
      return;
    }
    
    if (!phoneNumber.trim()) {
      setError('Phone number is required');
      setLoading(false);
      return;
    }
    
    // Basic phone number validation
    const phoneRegex = /^\+?1?\s*\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/;
    if (!phoneRegex.test(phoneNumber.trim())) {
      setError('Please enter a valid phone number (e.g., +1 (555) 123-4567)');
      setLoading(false);
      return;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      // Save user information to localStorage for the next step
      // No database write until payment completion
      const userInfo = {
        firstName,
        lastName,
        phoneNumber,
        timezone,
        email: email.trim(),
        password: password
      };
      localStorage.setItem('signupUserInfo', JSON.stringify(userInfo));
      
      // Navigate to loved one setup
      navigate('/signup/loved-one');
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load form data from localStorage on component mount
  useEffect(() => {
    const savedUserInfo = localStorage.getItem('signupUserInfo');
    if (savedUserInfo) {
      try {
        const userInfo = JSON.parse(savedUserInfo);
        if (userInfo.firstName) setFirstName(userInfo.firstName);
        if (userInfo.lastName) setLastName(userInfo.lastName);
        if (userInfo.email) setEmail(userInfo.email);
        if (userInfo.phoneNumber) setPhoneNumber(userInfo.phoneNumber);
        if (userInfo.timezone) setTimezone(userInfo.timezone);
        // Note: We don't restore password for security reasons
      } catch (err) {
        console.error('Error loading saved user info:', err);
      }
    }
  }, []);

  const handleBack = () => {
    // Save current form data before navigating back
    const userInfo = {
      firstName,
      lastName,
      phoneNumber,
      timezone,
      email: email.trim(),
      // Don't save password when going back
    };
    localStorage.setItem('signupUserInfo', JSON.stringify(userInfo));
    navigate('/signup');
  };

  return (
    <LandingLayout>
      <Container component="main" maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ width: '100%', maxWidth: 800, mb: 4 }}>
            <ProgressBar
              currentStep={2}
              totalSteps={5}
              stepLabels={['Plan', 'Account', 'Loved One', 'Care Circle', 'Payment']}
            />
          </Box>
          
          <Paper elevation={3} sx={{ p: 6, width: '100%', maxWidth: 600 }}>
            <Typography component="h1" variant="h4" align="center" gutterBottom sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
              Create Your Caregiver Account
            </Typography>
            
            <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
              Start your journey to peace of mind with daily wellness checks for your loved one
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {success}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    id="firstName"
                    label="My First Name"
                    name="firstName"
                    autoComplete="given-name"
                    autoFocus
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    disabled={loading}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    id="lastName"
                    label="My Last Name"
                    name="lastName"
                    autoComplete="family-name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    disabled={loading}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    id="email"
                    label="My Email Address"
                    name="email"
                    autoComplete="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    helperText="This will be your login email and where we'll send important notifications"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    id="phoneNumber"
                    label="My Phone Number"
                    name="phoneNumber"
                    autoComplete="tel"
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    disabled={loading}
                    helperText="We'll use this to send you important alerts and notifications"
                    placeholder="+1 (555) 123-4567"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    name="password"
                    label="My Password"
                    type="password"
                    id="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={handlePasswordChange}
                    disabled={loading}
                    error={password.length > 0 && !passwordValidation.valid}
                    helperText={
                      password.length > 0
                        ? passwordValidation.valid
                          ? 'Password meets requirements ✓'
                          : passwordValidation.issues.join(', ')
                        : 'Must be at least 6 characters long with uppercase, lowercase, numbers, and special characters'
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    name="confirmPassword"
                    label="Confirm My Password"
                    type="password"
                    id="confirmPassword"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    disabled={loading}
                    error={confirmPassword.length > 0 && password !== confirmPassword}
                    helperText={
                      confirmPassword.length > 0
                        ? password === confirmPassword
                          ? 'Passwords match ✓'
                          : 'Passwords do not match'
                        : 'Please confirm your password'
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
                    My Timezone
                  </Typography>
                  <TimezoneSelector 
                    onTimezoneChange={(newTimezone) => setTimezone(newTimezone)}
                    showCurrentTime={true}
                    allowSave={false}
                  />
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBack />}
                  onClick={handleBack}
                  disabled={loading}
                  sx={{
                    borderColor: '#2E7D32',
                    color: '#2E7D32',
                    '&:hover': {
                      borderColor: '#1B5E20',
                      backgroundColor: 'rgba(46, 125, 50, 0.04)',
                    },
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem'
                  }}
                >
                  Back
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  endIcon={<ArrowForward />}
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    px: 4,
                    backgroundColor: '#2E7D32',
                    '&:hover': {
                      backgroundColor: '#1B5E20'
                    },
                    fontSize: '1.1rem'
                  }}
                >
                  {loading ? 'Saving...' : 'Next'}
                </Button>
              </Box>

              <Box sx={{ textAlign: 'center' }}>
                <Link 
                  onClick={() => navigate('/login')} 
                  variant="body2" 
                  sx={{ cursor: 'pointer', color: '#2E7D32' }}
                >
                  Already have an account? Sign in
                </Link>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    </LandingLayout>
  );
};

export default Signup; 